/**
 * Fil conducteur de la démonstration : les étapes que suit un visiteur
 * qui découvre la maquette sans accompagnement.
 * La visite commence par le module Loisirs, qui est le focus de la démo.
 */
export interface DemoStep {
  /** Identifiant stable, utilisé pour marquer l'étape comme vue. */
  id: string
  /** Chapitre auquel appartient l'étape. */
  chapter: DemoChapterId
  /** Module visité à cette étape. */
  module: string
  /** Route ouverte quand on lance l'étape. */
  path: string
  /** Ce que le visiteur doit faire, en une phrase impérative. */
  action: string
  /** Ce qu'il va observer, pour comprendre l'intérêt de la fonction. */
  result: string
}

export type DemoChapterId = 'loisirs' | 'autres'

export const demoChapters: Record<DemoChapterId, { label: string; hint: string }> = {
  loisirs: {
    label: 'Focus : Mes loisirs',
    hint: 'Le parcours principal de la démonstration, de la recherche à l’inscription confirmée.',
  },
  autres: {
    label: 'Aperçu des autres modules',
    hint: 'À parcourir ensuite, pour situer les loisirs dans l’ensemble des services.',
  },
}

export const demoIntro =
  'Maquette interactive : tout ce qui est décrit ci-dessous fonctionne réellement. Les données sont fictives.'

export const demoSteps: DemoStep[] = [
  {
    id: 'loisirs-accueil',
    chapter: 'loisirs',
    module: 'Accueil → Loisirs',
    path: '/',
    action: 'Écrivez « inscrire mon enfant à la natation » dans la barre « Demander à ATNA », à droite du héro.',
    result: 'Le parcours d’inscription s’ouvre depuis la page d’accueil, sans chercher dans les menus.',
  },
  {
    id: 'loisirs-inscription',
    chapter: 'loisirs',
    module: 'Mes loisirs · Inscription',
    path: '/mes-loisirs',
    action: 'Cliquez la pastille foncée « Inscrire mon enfant » sous la barre ATNA.',
    result: 'ATNA demande l’activité, puis propose les créneaux réellement disponibles pour Emma, 9 ans.',
  },
  {
    id: 'loisirs-creneau',
    chapter: 'loisirs',
    module: 'Mes loisirs · Confirmation',
    path: '/mes-loisirs',
    action: 'Dans la conversation, choisissez le créneau recommandé, puis confirmez l’inscription d’Emma.',
    result: 'Inscription complétée de bout en bout : aucun formulaire, aucun panier, aucun conflit d’horaire.',
  },
  {
    id: 'loisirs-recherche',
    chapter: 'loisirs',
    module: 'Mes loisirs · Recherche',
    path: '/mes-loisirs',
    action: 'Relancez la barre ATNA avec « soccer », « patin » ou « guitare ».',
    result: 'ATNA reconnaît la discipline et affiche lieux, horaires, tarifs et places restantes.',
  },
  {
    id: 'loisirs-pret',
    chapter: 'loisirs',
    module: 'Mes loisirs · Préparation',
    path: '/mes-loisirs',
    action: 'Dans « Prêt pour votre inscription », cliquez « Corriger maintenant ».',
    result: 'Le seul blocage avant l’ouverture des inscriptions est levé d’avance, sans appel au service.',
  },
  {
    id: 'loisirs-alerte',
    chapter: 'loisirs',
    module: 'Mes loisirs · Alertes',
    path: '/mes-loisirs',
    action: 'Sur la carte « Pickleball libre », cliquez « Me prévenir à l’ouverture », puis choisissez SMS.',
    result: 'ATNA surveille l’ouverture et les places qui se libèrent, et prévient sur le bon canal.',
  },
  {
    id: 'loisirs-reservation',
    chapter: 'loisirs',
    module: 'Mes loisirs · Réservations',
    path: '/mes-loisirs',
    action: 'Descendez à « Réserver un espace » et « Louer de l’équipement », puis cliquez « Réserver ».',
    result: 'Salles, plateaux et équipement se réservent au même endroit que les inscriptions.',
  },
  {
    id: 'demandes',
    chapter: 'autres',
    module: 'Mes demandes',
    path: '/mes-demandes',
    action: 'Cliquez la pastille « Route / nid-de-poule », puis suivez la conversation jusqu’au dépôt.',
    result: 'La demande apparaît dans la liste et sur la carte des signalements du quartier.',
  },
  {
    id: 'collectes',
    chapter: 'autres',
    module: 'Mes collectes',
    path: '/mes-collectes',
    action: 'Cliquez la pastille « Bac non ramassé » dans la barre ATNA.',
    result: 'ATNA répond et ouvre automatiquement une demande aux Travaux publics, sans formulaire.',
  },
  {
    id: 'animaux',
    chapter: 'autres',
    module: 'Mes animaux',
    path: '/mes-animaux',
    action: 'Ouvrez la fiche d’un animal, puis cliquez « Déclarer … perdu ».',
    result: 'Un avis de recherche est diffusé avec un rayon de recherche et les signalements reçus.',
  },
  {
    id: 'carte',
    chapter: 'autres',
    module: 'Ma carte',
    path: '/ma-carte',
    action: 'Ouvrez un membre de la famille et modifiez sa carte.',
    result: 'La carte citoyenne qui donne accès aux activités se gère pour toute la famille.',
  },
  {
    id: 'avis',
    chapter: 'autres',
    module: 'Avis et notifications',
    path: '/notifications',
    action: 'Cliquez les filtres « Mes alertes importantes » ou « Non lus ».',
    result: 'Le fil d’avis se recentre sur ce qui vous concerne vraiment.',
  },
  {
    id: 'plan',
    chapter: 'autres',
    module: 'Plan de la ville',
    path: '/plan',
    action: 'Filtrez par catégorie, puis sélectionnez un lieu sur la carte.',
    result: 'Les installations municipales sont localisées avec l’itinéraire en un clic.',
  },
]
